import DefaultContentTemplateTags from '@/components/DefaultContentTemplate/Tags';
import { Enum_Like_Type, WildcardEntity } from '@/graphql/generated/graphql';
import { countLikes } from '@/services/global/like.service';
import { ClockIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAnnouncementStore } from '../../../store/announcementStore';

export const AnnouncementInfo = ({ wildcard, actions }: { wildcard: WildcardEntity; actions: React.ReactNode }) => {
  const likeData = { id: parseInt(wildcard.id ?? '') || 0, type: Enum_Like_Type.Wildcard };
  const { page } = useAnnouncementStore();

  const { data } = useQuery({
    queryKey: ['likeCount', likeData],
    queryFn: () => countLikes(likeData),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const statusLabels = {
    live: page?.preview?.liveLabel || 'Live',
    scheduled: page?.preview?.scheduledLabel || 'Scheduled',
    liveFrom: page?.preview?.liveFromLabel || 'Live from',
  };

  // Live, Scheduled, Live from base on start and end date
  const status =
    dayjs().isAfter(dayjs(wildcard.attributes?.startDate)) && dayjs().isBefore(dayjs(wildcard.attributes?.endDate))
      ? statusLabels.live
      : dayjs().isAfter(dayjs(wildcard.attributes?.startDate))
        ? statusLabels.liveFrom
        : statusLabels.scheduled;

  const getOrdinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const formatDate = (date: string) => {
    const d = dayjs(date);
    return `${getOrdinal(d.date())} ${d.format('MMMM')}`;
  };

  const untilLabel = page?.preview?.untilLabel || 'until';

  // if live "until January 13"
  // if schedule "January 13"
  // if live from "10th - 13th January"
  const until =
    status === statusLabels.live
      ? `${untilLabel} ${formatDate(wildcard.attributes?.endDate)}`
      : status === statusLabels.scheduled
        ? formatDate(wildcard.attributes?.startDate)
        : `${formatDate(wildcard.attributes?.startDate)} - ${formatDate(wildcard.attributes?.endDate)}`;

  const icons = {
    [statusLabels.live]: <InformationCircleIcon className="size-5" />,
    [statusLabels.scheduled]: <ClockIcon className="size-5" />,
    [statusLabels.liveFrom]: <></>,
  };

  const totalLikesLabel = page?.preview?.totalLikesLabel || 'Total likes';

  return (
    <div className="relative flex flex-col items-center justify-center">
      <DefaultContentTemplateTags tags={[wildcard.attributes?.status || '']} className="!mb-sh-15 !mt-0" />
      <div className="flex flex-col items-center justify-center">
        <div className="flex items-center justify-center gap-2">
          {icons[status]}
          <p className="text-sh-sm font-bold">{status}</p>
        </div>
        <span className=" text-sh-xxs">{until}</span>

        <p className="py-sh-15 text-sh-sm">
          <strong>{totalLikesLabel}</strong> {data?.data.likes || 0}
        </p>
        {actions}
      </div>
    </div>
  );
};
